import React from 'react';
import {
  Paper,
  Typography,
  Button,
  Box,
  CircularProgress,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  PlayArrow as ExecuteIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

/**
 * TemplateActions Component
 *
 * Component containing the main action buttons for saving and canceling
 * template operations.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.saving - Whether save operation is in progress
 * @param {boolean} props.updatingMetadata - Whether metadata update is in progress
 * @param {boolean} props.isEditMode - Whether the editor is in edit mode
 * @param {string} props.templateId - Template ID for navigation
 * @param {string} props.versionId - Version ID if editing a specific version
 * @param {string} props.currentVersionStatus - Current version status (draft, active, etc.)
 * @param {Function} props.handleSave - Handler for save action (creates new version)
 * @param {Function} props.handleUpdateMetadata - Handler for metadata-only update
 * @param {Function} props.handleUpdateDraft - Handler for updating draft version
 * @param {Function} props.handleCancel - Handler for cancel action
 */
const TemplateActions = ({
  saving,
  updatingMetadata,
  isEditMode,
  templateId,
  versionId,
  currentVersionStatus,
  handleSave,
  handleUpdateMetadata,
  handleUpdateDraft,
  handleCancel,
}) => {
  const navigate = useNavigate();

  const handleExecuteTemplate = () => {
    if (templateId) {
      navigate(`/templates/${templateId}/execute`);
    }
  };
  // When editing a specific version, don't show "Update Template Header" button
  const isVersionEdit = Boolean(versionId);
  const isDraftVersion = currentVersionStatus === 'draft';
  return (
    <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
        Actions
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {isEditMode ? (
          <>
            {!isVersionEdit && (
              <Button
                fullWidth
                variant="outlined"
                color="primary"
                onClick={handleUpdateMetadata}
                disabled={updatingMetadata || saving}
                startIcon={
                  updatingMetadata ? (
                    <CircularProgress size={20} />
                  ) : (
                    <SaveIcon />
                  )
                }
              >
                {updatingMetadata ? 'Updating...' : 'Update Template Header'}
              </Button>
            )}
            {isVersionEdit && isDraftVersion && (
              <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={handleUpdateDraft}
                disabled={saving || updatingMetadata}
                startIcon={
                  saving ? <CircularProgress size={20} /> : <SaveIcon />
                }
              >
                {saving ? 'Updating...' : 'Update Draft'}
              </Button>
            )}
            {(!isVersionEdit || !isDraftVersion) && (
              <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={handleSave}
                disabled={saving || updatingMetadata}
                startIcon={
                  saving ? <CircularProgress size={20} /> : <SaveIcon />
                }
              >
                {saving ? 'Creating...' : 'Create New Version'}
              </Button>
            )}
          </>
        ) : (
          <Button
            fullWidth
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
          >
            {saving ? 'Creating...' : 'Create Template'}
          </Button>
        )}

        {isEditMode && templateId && (
          <Button
            type="button"
            fullWidth
            variant="outlined"
            startIcon={<ExecuteIcon />}
            onClick={handleExecuteTemplate}
          >
            Execute Template
          </Button>
        )}

        <Button
          fullWidth
          variant="outlined"
          onClick={handleCancel}
          startIcon={<CancelIcon />}
        >
          Cancel
        </Button>
      </Box>
    </Paper>
  );
};

export default TemplateActions;
